/**
 * features/exams/dutyStyle.ts — what a duty looks like, in one place.
 *
 * FCS (the key-time window) IS A DIFFERENT JOB from invigilating, so it is a
 * different colour (the school, 2026-09-18): you are due in before the paper
 * starts, often for a window nobody else on the rota is working. On a screen
 * of otherwise identical cards that is the single fact most easily missed.
 *
 * VIOLET, not amber or rose. Those two already mean something on these
 * screens — amber is "this duty falls on a lesson you teach" and rose is "no
 * invigilator" — and a colour that means two things means neither. Violet is
 * in the kit's own accent list, so it stays inside the palette.
 *
 * Both panes import from here rather than each spelling the classes out: the
 * whole point is that an FCS card looks the same wherever it appears, and two
 * copies of a colour drift the first time one is adjusted.
 *
 * Every colour repeats under `dark:` — the global `.dark p/span/div` rules in
 * index.css are (0,1,1) and beat a plain utility (see CLAUDE.md).
 */
import type { DutyRole } from '@/lib/examDuties';

/** The office's own word for the key-time window, on their own spreadsheet. */
export const ROLE_LABEL: Record<DutyRole, string> = {
  keyTime: 'FCS · key time',
  invigilator: 'invigilator',
};

/** Short form, where the cell is narrow. */
export const ROLE_SHORT: Record<DutyRole, string> = {
  keyTime: 'FCS',
  invigilator: 'invigilator',
};

/** The little uppercase pill on a card or beside a name. */
export const ROLE_CHIP: Record<DutyRole, string> = {
  keyTime: 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300',
  invigilator: 'bg-[var(--sp-line)] text-[var(--sp-body)]',
};

/** A whole session card: violet ground and edge when FCS is part of it. */
export const ROLE_CARD: Record<DutyRole, string> = {
  keyTime: 'border-violet-200 bg-violet-50/70 dark:border-violet-900/60 dark:bg-violet-950/25',
  invigilator: 'border-[var(--sp-line)] bg-[var(--sp-paper)]',
};

/** One person's chip in the invigilation rota. */
export const ROLE_NAME_CHIP: Record<DutyRole, string> = {
  keyTime: 'border-violet-200 bg-violet-50 dark:border-violet-900/60 dark:bg-violet-950/40',
  invigilator: 'border-[var(--sp-line)] bg-[var(--sp-paper)]',
};

export const ROLE_ICON: Record<DutyRole, string> = {
  keyTime: 'text-violet-700 dark:text-violet-300',
  invigilator: 'text-[var(--sp-muted)]',
};

/** A session's own role: FCS wins, because that is the earlier call-time. */
export function leadRole(roles: DutyRole[]): DutyRole {
  return roles.includes('keyTime') ? 'keyTime' : 'invigilator';
}
